import { Box, Button, CircularProgress, Grid, IconButton, Modal, Paper, Tab, Tabs, Typography } from "@mui/material";
import AlertModal from "../AlertModal";
import CloseIcon from "@mui/icons-material/Close";
import CustomDateInput from "../customComponents/CustomDateInput";
import CustomInput from "../customComponents/CustomInput";
import CustomMultiSelect from "../customComponents/CustomMultiSelect";
import CustomNumber from "../customComponents/CustomNumber";
import CustomPhonesUpload from "../customComponents/CustomPhonesUpload";
import CustomSelect from "../customComponents/CustomSelect";
import CustomToggle from "../customComponents/CustomToggle";
import PopupImage from "./PopupImage";
import { postPopupAdd } from "../../slice/PopupAddSlice";
import { putPopupEdit } from "../../slice/PopupEditSlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

const PopupForm = ({data, isOpen, onClose}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    
    const [alertMessage, setAlertMessage] = useState("");
    const [alertType, setAlertType] = useState("success");
    const [errors, setErrors] = useState({});
    const [isAlertOpen, setAlertOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isChanged, setIsChanged] = useState(false);
    const [tabValue, setTabValue] = useState("tabDetails");
    
    const [popupId, setPopupId] = useState(0);
    const [name, setName] = useState("");
    const [active, setActive] = useState(false);
    const [startAt, setStartAt] = useState("");
    const [endAt, setEndAt] = useState("");
    const [targetPlatform, setTargetPlatform] = useState("All");
    const [targetUsers, setTargetUsers] = useState("RegionalUsers");
    const [targetScreens, setTargetScreens] = useState(["HomeScreen"]);
    const [maxPopups, setMaxPopups] = useState(0);
    const [onClickAction, setOnClickAction] = useState("None");
    const [onClickURL, setOnClickURL] = useState("");
    const [onClickScreen, setOnClickScreen] = useState("");
    const [images, setImages] = useState([]);
    const [targetUserPhones, setTargetUserPhones] = useState([]);
    const [targetStations, setTargetStations] = useState([]);
    
    const isError = useSelector((state) => (isEditMode ? state.putPopupEdit?.error : state.postPopupAdd?.error));
    const isLoading = useSelector((state) => (isEditMode ? state.putPopupEdit?.isLoading : state.postPopupAdd?.isLoading));
    const stationList = useSelector((state) => state.stationList?.data?.data);

    useEffect(() => {
        setIsChanged(false);
        if (data !== null) {
            setIsEditMode(true);
            setPopupId(data?.id || 0);
            setName(data?.name || "");
            setActive(data?.is_active || false);
            setStartAt(data?.start_at || "");
            setEndAt(data?.end_at || "");
            setTargetPlatform(data?.target_platform || "All");
            setTargetUsers(data?.target_users || "RegionalUsers");
            setTargetScreens(data?.target_screens || ["HomeScreen"]);
            setMaxPopups(data?.max_popups || 0);
            setOnClickAction(data?.on_click_action || "None");
            setOnClickURL(data?.on_click_url || "");
            setOnClickScreen(data?.on_click_screen || "");
            setImages(data?.images || []);
            setTargetUserPhones(data?.target_user_phones || []);
            setTargetStations(data?.target_stations || []);
        } else {
            const today = new Date();
            const formattedDate = today.toISOString().split("T")[0];
            setPopupId(0);
            setIsEditMode(false);
            setName("");
            setActive(false);
            setStartAt(formattedDate);
            setEndAt(formattedDate);
            setTargetPlatform("All");
            setTargetUsers("RegionalUsers");
            setTargetScreens(["HomeScreen"]);
            setMaxPopups(0);
            setOnClickAction("None");
            setOnClickURL("");
            setOnClickScreen("");
            setImages([]);
            setTargetUserPhones([]);
            setTargetStations([]);
        }
    }, [data]);

    async function handleDataAdd() { 
        const response = await dispatch(
          postPopupAdd({
            name: name,
            is_active: active,
            start_at: startAt,
            end_at: endAt,
            target_platform: targetPlatform,
            target_users: targetUsers,
            target_user_phones: targetUserPhones,
            target_screens: targetScreens,
            max_popups: maxPopups,
            on_click_action: onClickAction,
            on_click_url: onClickURL,
            on_click_screen: onClickScreen,
            target_stations: targetStations,
          }),
        ).unwrap();

        handleShowAlert(response?.message, response?.success ? "success" : "error");
        if (!response?.success) {
            setErrors(response?.data || {});
        } else {
            setPopupId(response?.data?.id || 0);
            setIsEditMode(true);
            setErrors({});
            setIsChanged(true);
            setTabValue("tabImages");
        }
    };

    async function handleDataSave() {
        console.log({
          id: popupId,
          name: name,
          is_active: active,
          start_at: startAt,
          end_at: endAt,
          target_platform: targetPlatform,
          target_users: targetUsers,
          target_screens: targetScreens,
          max_popups: maxPopups,
          on_click_action: onClickAction,
          on_click_url: onClickURL,
          on_click_screen: onClickScreen,
          images: images,
          target_user_phones: targetUserPhones,
          target_stations: targetStations,
        });
        const response = await dispatch(putPopupEdit({ 
            id: popupId,
            name: name,
            is_active: active,
            start_at: startAt,
            end_at: endAt,  
            target_platform: targetPlatform,
            target_users: targetUsers,
            target_screens: targetScreens,
            max_popups: maxPopups,  
            on_click_action: onClickAction,
            on_click_url: onClickURL,   
            on_click_screen: onClickScreen,
            images: images,
            target_user_phones: targetUserPhones,
            target_stations: targetStations,
        })).unwrap();

        handleShowAlert(response?.message, response?.success ? "success" : "error");
        if (!response?.success) {
            setErrors(response?.data || {});
        } else {
            setPopupId(response?.data?.id || 0);
            setIsEditMode(true);
            setErrors({});
            setIsChanged(true);
        }        
    };

    function handleEndAtChange(value) {
        const { $D, $M, $y } = value;
        setEndAt(`${$y}-${$M + 1}-${$D}`);
    };

    const handleImageAdded = useCallback((newImageUrl) => {
        setImages([...images, newImageUrl]);
        setIsChanged(true);
    }, [images]);

    const handleImageDeleted = useCallback((oldImageUrl) => {
        setImages(images.filter((image) => image !== oldImageUrl));
        setIsChanged(true);
    }, [images]);


    function handlePhonesUploadChange(value) {
        setTargetUserPhones(value); 
        setIsChanged(true);
    };

    function handleShowAlert(message, type) {
        setAlertMessage(message);
        setAlertType(type);
        setAlertOpen(true);
    };

    function handleStartAtChange(value) {
        const { $D, $M, $y } = value;
        setStartAt(`${$y}-${$M + 1}-${$D}`);
    }

    function handleTabChange(_, newValue) {
        setTabValue(newValue);
    };   

    const handleTargetScreensChange = useCallback((value) => {
        const currentStations = new Set();

        if (value.includes("AllStationsScreen")) {
            stationList.forEach(el => currentStations.add(el.id));
        }
        if (value.includes("PrivateStationsScreen")) {
            stationList.filter(el => el.is_private).forEach(el => currentStations.add(el.id));
        }
        if (value.includes("PublicStationsScreen")) {
            stationList.filter(el => !el.is_private).forEach(el => currentStations.add(el.id));
        }
        if (value.includes("PenaltyStationsScreen")) {
            stationList.filter(el => el.is_penalty_enable).forEach(el => currentStations.add(el.id));
        }
        if (value.includes("NonPenaltyStationsScreen")) {
            stationList.filter(el => !el.is_penalty_enable).forEach(el => currentStations.add(el.id));
        }

        setTargetStations(Array.from(currentStations));
        setTargetScreens(value);
    }, [stationList]);

    function renderLoading() {
        return (
            <Grid container justifyContent="center" alignItems="center" sx={{ height: "100%" }} >
                <CircularProgress />
            </Grid>
        );
    };

    const renderDetailsContent = useCallback(() => {
        return (
          <Grid
            container
            spacing={{ xs: 2 }}
            columns={{ xs: 4, sm: 2, md: 12 }}
            style={{
              maxHeight: "85vh",
              overflow: "auto",
              paddingTop: "24px",
              paddingBottom: "54px",
            }}
          >
            <CustomInput
              col_size={12}
              disabled={isEditMode}
              errorMessage={errors["name"]}
              isError={errors["name"]}
              label={"Popup Name"}
              onValueChange={(value) => setName(value)}
              placeholder={"Input unique name"}
              required={true}
              value={name}
            />
            <CustomToggle
              label={"Active"}
              onValueChange={(value) => setActive(value)}
              value={active}
            />
            <CustomDateInput
              errorMessage={errors["start_at"]}
              isError={errors["start_at"]}
              col_size={6}
              label={"Start Date"}
              onValueChange={handleStartAtChange}
              required={true}
              value={startAt}
              disablePast={true}
            />
            <CustomDateInput
              errorMessage={errors["end_at"]}
              isError={errors["end_at"]}
              col_size={6}
              label={"End Date"}
              onValueChange={handleEndAtChange}
              required={true}
              value={endAt}
              disablePast={true}
            />
            <CustomSelect
              col_size={6}
              label={"Target Platform"}
              onValueChange={(value) => setTargetPlatform(value)}
              options={[
                { value: "All", label: "All" },
                { value: "Android", label: "Android" },
                { value: "iOS", label: "iOS" },
              ]}
              required={true}
              value={targetPlatform}
            />
            <CustomSelect
              col_size={6}
              label={"Target Users"}
              onValueChange={(value) => setTargetUsers(value)}
              options={[
                { value: "RegionalUsers", label: "Regional Users" },
                { value: "SpecificUsers", label: "Specific Users" },
              ]}
              required={true}
              value={targetUsers}
            />
            {targetUsers === "SpecificUsers" && (
              <CustomPhonesUpload
                col_size={12}
                errorMessage={errors["target_user_phones"]}
                isError={errors["target_user_phones"]}
                label={"Target User Phones"}
                onValueChange={(value) => handlePhonesUploadChange(value)}
                required={true}
                value={targetUserPhones}
              />
            )}
            <CustomMultiSelect
              col_size={12}
              errorMessage={errors["target_screens"]}
              isError={errors["target_screens"]}
              label={"Target Screens"}
              options={[
                { value: "HomeScreen", label: "Home" },
                { value: "AllStationsScreen", label: "All Stations" },
                { value: "PrivateStationsScreen", label: "Private Stations" },
                { value: "PublicStationsScreen", label: "Public Stations" },
                { value: "PenaltyStationsScreen", label: "Penalty Stations" },
                {
                  value: "NonPenaltyStationsScreen",
                  label: "Non Penalty Stations",
                },
                { value: "SpecificStationsScreen", label: "Specific Stations" },
                { value: "WalletScreen", label: "Wallet" },
                {
                  value: "ChargingCompletedScreen",
                  label: "Charging Completed",
                },
              ]}
              required={true}
              onValueChange={(value) => handleTargetScreensChange(value)}
              value={targetScreens}
            />
            {targetScreens.includes("SpecificStationsScreen") && (
              <CustomMultiSelect
                col_size={12}
                errorMessage={errors["target_stations"]}
                isError={errors["target_stations"]}
                label={"Target Stations"}
                options={stationList.map((el) => {
                  return {
                    label: el.name,
                    value: el.id,
                  };
                })}
                onValueChange={(value) => {
                  console.log(value);
                  setTargetStations(value);
                }}
                required={true}
                value={targetStations}
              />
            )}
            <CustomNumber
              col_size={6}
              label={"Max Popups"}
              onValueChange={(value) => setMaxPopups(value)}
              value={maxPopups}
            />
            <CustomSelect
              col_size={6}
              label={"On Click Action"}
              onValueChange={(value) => setOnClickAction(value)}
              options={[
                { value: "None", label: "None" },
                { value: "OpenURL", label: "Open URL" },
                { value: "OpenScreen", label: "Open Screen" },
              ]}
              value={onClickAction}
            />
            {onClickAction === "OpenURL" && (
              <CustomInput
                col_size={12}
                errorMessage={errors["on_click_url"]}
                isError={errors["on_click_url"]}
                label={"Action Target URL"}
                onValueChange={(value) => setOnClickURL(value)}
                required={true}
                value={onClickURL}
              />
            )}
            {onClickAction === "OpenScreen" && (
              <CustomSelect
                col_size={12}
                errorMessage={errors["on_click_screen"]}
                isError={errors["on_click_screen"]}
                label={"Action Target Screen"}
                onValueChange={(value) => setOnClickScreen(value)}
                options={[
                  { value: "HomeScreen", label: "Home" },
                  { value: "WalletScreen", label: "Wallet" },
                ]}
                required={true}
                value={onClickScreen}
              />
            )}
          </Grid>
        );
    }, [isEditMode, errors, name, active, startAt, endAt, targetPlatform, targetUsers, targetUserPhones, targetScreens, handleTargetScreensChange,
        stationList, targetStations, maxPopups, onClickAction, onClickURL, onClickScreen]
    );

    function renderSubmitterError () {
        return (
            <Grid container justifyContent="center" alignItems="center" sx={{ height: "100%" }} >
                <Typography> Some error occurred! Please contact your administrator. </Typography>
            </Grid>
        );
    };

    const renderTabContent = useMemo(() => {
        switch (tabValue) {
            case "tabDetails":
                return renderDetailsContent();
            case "tabImages":
                return (
                    <Box sx={{ padding: 2 }}>
                        <PopupImage id={popupId} label={"Image URL"} images={images} onImageAdded={handleImageAdded} onImageDeleted={handleImageDeleted}/>
                    </Box>
                );
            default:
                return <Box sx={{ padding: 2 }}></Box>;
        }
    }, [tabValue, popupId, images, handleImageAdded, handleImageDeleted, renderDetailsContent]);

    const renderTabs = useMemo(() => {
    return (
        <Box sx={{ maxWidth: { xs: 320, sm: 700 }, bgcolor: "white", borderBottom: "1px solid #EAEBEE", zIndex:111 }} style={{maxWidth: "100%"}} >
            <Tabs value={tabValue} onChange={ handleTabChange } variant="scrollable" scrollButtons={false}>
                <Tab label={"Details"} value={"tabDetails"} />
                { (isEditMode) && <Tab label={"Images"} value={"tabImages"} /> }
            </Tabs>
        </Box>
        )
    }, [tabValue, isEditMode]);

    return (
        <>
        <Modal closeAfterTransition onClose={() => onClose(isChanged)} open={isOpen} >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", height: "100%" }} >
                <Paper sx={{ width: "595px", height: "100%" }} >
                    <IconButton onClick={onClose} sx={{ position: "absolute", top: "8px", right: "8px", color: "var(--light-mode-gray-80, #5C677D)" }} >
                        <CloseIcon />
                    </IconButton>
                    {isLoading ? 
                        renderLoading() 
                    : isError ? 
                        renderSubmitterError()
                    : (
                        <Box p={3}>
                            <Grid container direction="column">
                                <Typography variant="h6">{ isEditMode ? "Edit Popup Banner": "Add Popup Banner" }</Typography>

                                <Grid style={{ padding: "8px 16px 0 16px" }}>
                                    { renderTabs }
                                    { renderTabContent }
                                </Grid>

                            </Grid>
                            <Box style={{ position: "sticky", bottom: 0, backgroundColor: "white", height: "72px", display: "flex", zIndex: 111, justifyContent: "flex-end", paddingTop: "16px", borderTop: "1px solid #EAEBEE"}}>
                                {(!isEditMode && (
                                    <Button 
                                        variant="contained" 
                                        style={{ borderRadius: "28px", padding: "16px", height: "40px", backgroundColor: "#467CEB", textTransform: "capitalize" }}
                                        onClick={() => handleDataAdd()}
                                    >
                                        {t("add")}
                                    </Button>
                                ))}
                                {(isEditMode && (
                                    <Button 
                                        variant="contained" 
                                        style={{ borderRadius: "28px", padding: "16px", height: "40px", backgroundColor: "#467CEB", textTransform: "capitalize" }}
                                        onClick={() => handleDataSave()}
                                    >
                                        {t("save")}
                                    </Button>
                                ))}
                            </Box>
                        </Box>   
                    )}
                </Paper>
            </Box>
        </Modal>

        <AlertModal open={isAlertOpen} onClose={() => setAlertOpen(false)} message={alertMessage} alertType={alertType} onModalClose={() => {}} dynamicDispatch={() => {}}/>
        </>            
    )};

export default PopupForm;